import {FormControl, FormField, ProgressBar, Segment} from 'platform/components';
import {Box, Heading, HStack, Severity, Show, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useId} from 'react';
import {useWatch} from 'react-hook-form';

import {TitleExtensionLanguage} from '@dms/api/sales';
import i18n from '@dms/i18n';

import {getPreviewShortNames} from '../utils/getPreviewShortNames';
import {TitleExtensionEquipmentFormValues} from './titleExtensionEquipmentDraftSchema';

interface TitleExtensionEquipmentSidebarProps {
  control: FormControl<TitleExtensionEquipmentFormValues>;
  activeLanguage?: TitleExtensionLanguage;
  isEditable: boolean;
  isInteractionDisabled: boolean;
  onSeparatorPresetChange: (separator: string) => void;
}

// Bare characters, no padding: every character counts against the platform title
// limits, so spaces around the separator are the dealer's decision to make in the
// free-text field, not something a preset spends on their behalf.
const SEPARATOR_OPTIONS = [
  {label: '·', value: '·'},
  {label: ',', value: ','},
  {label: '|', value: '|'},
  {label: '/', value: '/'},
  {label: '*', value: '*'},
];
const SAMPLE_VEHICLE_TITLES = [
  'Škoda Octavia Combi 2.0 TDI Style DSG',
  'BMW 320d xDrive Touring M Sport',
];

/**
 * Reference length used to scale the preview bar and counter. It is NOT a real limit — the
 * actual budget is per platform and per vehicle and is not knowable from Settings, which is
 * why the surrounding card is labelled an illustrative preview. The severity steps exist to
 * make the effect of separator padding and short-name length legible at a glance.
 */
const PREVIEW_TITLE_REFERENCE_LENGTH = 80;
const PREVIEW_WARNING_RATIO = 0.9;
const PREVIEW_OVER_LIMIT_RATIO = 1;

// `as const` keeps each handler inferring a literal; without it ts-pattern widens them to
// `string` and the `Severity` return annotation stops compiling.
const getPreviewSeverity = (ratio: number): Severity =>
  match(ratio)
    .when(
      (value) => value > PREVIEW_OVER_LIMIT_RATIO,
      () => 'danger' as const
    )
    .when(
      (value) => value >= PREVIEW_WARNING_RATIO,
      () => 'warning' as const
    )
    .otherwise(() => 'success' as const);

export function TitleExtensionEquipmentSidebar(props: TitleExtensionEquipmentSidebarProps) {
  const separatorInputId = useId();
  const [items = [], separator = ''] = useWatch({
    control: props.control,
    name: ['items', 'separator'],
  });
  const normalizedSeparator = separator ?? '';

  return (
    <VStack spacing={4}>
      <Box
        backgroundColor="palettes.white.10.100"
        borderRadius="medium"
        boxShadow="elevation_1"
        padding={4}
      >
        <VStack spacing={4}>
          <VStack spacing={1}>
            <Heading size={4}>{i18n.t('page.titleExtensionEquipment.labels.separator')}</Heading>
            <Text size="small" color="secondary">
              {i18n.t('page.titleExtensionEquipment.labels.separatorHint')}
            </Text>
          </VStack>

          <Show when={props.isEditable}>
            <HStack spacing={2} align="flex-start" wrap>
              <Box flex={1} minWidth={40}>
                <Segment
                  label={i18n.t('page.titleExtensionEquipment.labels.separator')}
                  value={normalizedSeparator}
                  options={SEPARATOR_OPTIONS}
                  onChange={props.onSeparatorPresetChange}
                  isDisabled={props.isInteractionDisabled}
                />
              </Box>
              <Box width={24}>
                <Box position="absolute" width={0} height={0} overflow="hidden">
                  <label htmlFor={separatorInputId}>
                    {i18n.t('page.titleExtensionEquipment.labels.separator')}
                  </label>
                </Box>
                <FormField
                  id={separatorInputId}
                  control={props.control}
                  type="text"
                  name="separator"
                  maxLength={10}
                  isRequired
                  isDisabled={props.isInteractionDisabled}
                />
              </Box>
            </HStack>
          </Show>

          <Show when={!props.isEditable}>
            <Text>{normalizedSeparator}</Text>
          </Show>
        </VStack>
      </Box>

      <TitleExtensionEquipmentPreview
        items={items}
        separator={normalizedSeparator}
        activeLanguage={props.activeLanguage}
      />
    </VStack>
  );
}

interface TitleExtensionEquipmentPreviewProps {
  items: TitleExtensionEquipmentFormValues['items'];
  separator: string;
  activeLanguage?: TitleExtensionLanguage;
}

function TitleExtensionEquipmentPreview(props: TitleExtensionEquipmentPreviewProps) {
  const previewShortNames = getPreviewShortNames(props.items, props.activeLanguage);
  const previewSuffix = previewShortNames.join(props.separator);

  return (
    <Box
      backgroundColor="palettes.white.10.100"
      borderRadius="medium"
      boxShadow="elevation_1"
      padding={4}
    >
      <VStack spacing={4}>
        <VStack spacing={1}>
          <Heading size={4}>
            {i18n.t('page.titleExtensionEquipment.labels.illustrativePreview')}
          </Heading>
          <Text size="small" color="secondary">
            {i18n.t('page.titleExtensionEquipment.labels.previewHint')}
          </Text>
        </VStack>

        <VStack spacing={3}>
          {SAMPLE_VEHICLE_TITLES.map((vehicleTitle) => {
            // Counts exactly what is rendered above the bar — the placeholder shown when
            // nothing is mapped yet is not part of a real title, so it is not counted.
            const composedTitle =
              previewSuffix.length > 0 ? `${vehicleTitle} ${previewSuffix}` : vehicleTitle;
            const ratio = composedTitle.length / PREVIEW_TITLE_REFERENCE_LENGTH;

            return (
              <VStack key={vehicleTitle} spacing={1}>
                <Box
                  backgroundColor="palettes.neutral.10.100"
                  border="1px solid"
                  borderColor="general.separator"
                  borderRadius="small"
                  padding={3}
                >
                  <Text overflowWrap="anywhere">
                    <Text inline>{vehicleTitle}</Text>
                    <Show when={previewSuffix.length > 0}>
                      <Text inline color="link">
                        {` ${previewSuffix}`}
                      </Text>
                    </Show>
                    <Show when={previewSuffix.length === 0}>
                      <Text inline color="tertiary">
                        {` ${i18n.t('page.titleExtensionEquipment.labels.noShortName')}`}
                      </Text>
                    </Show>
                  </Text>
                </Box>
                <HStack spacing={2} align="center">
                  <Box flex={1}>
                    <ProgressBar percentage={ratio} severity={getPreviewSeverity(ratio)} small />
                  </Box>
                  <Text size="xSmall" color="secondary" noWrap>
                    {`${composedTitle.length} / ${PREVIEW_TITLE_REFERENCE_LENGTH}`}
                  </Text>
                </HStack>
              </VStack>
            );
          })}
        </VStack>
      </VStack>
    </Box>
  );
}
